import {
  resolveEffectiveScopeInterventionMode,
  scopeInterventionModesOverlap,
} from '@evimo/core/modules/partner-maintenance-rules/domain/partner-maintenance-scope-mode.utils';
import type {
  PartnerMaintenanceInterventionMode,
  PartnerMaintenanceScopeDimension,
  PartnerMaintenanceScopeEffect,
  PartnerMaintenanceScopeInterventionMode,
} from '@evimo/types/enums/partner-maintenance-rules';

export type ScopeHierarchyAsset = {
  id: string;
  assetModelId: string | null;
  manufacturerId: string | null;
  cityCode: string | null;
};

export type ScopeIncludeFlatAtom = {
  ruleId: string;
  dimension: PartnerMaintenanceScopeDimension;
  effect: PartnerMaintenanceScopeEffect;
  value: string;
  interventionMode: PartnerMaintenanceScopeInterventionMode | null;
};

export type ScopeHierarchyRedundancy = {
  broaderRuleId: string;
  broaderDimension: PartnerMaintenanceScopeDimension;
  broaderValue: string;
  narrowerRuleId: string;
  narrowerDimension: PartnerMaintenanceScopeDimension;
  narrowerValue: string;
};

type ScopeRuleLike = {
  id?: string;
  dimension: string;
  effect: PartnerMaintenanceScopeEffect;
  value?: string | null;
  values?: string[];
  interventionMode?: PartnerMaintenanceScopeInterventionMode | null;
};

const BROADER_NARROWER_PAIRS: Array<
  [PartnerMaintenanceScopeDimension, PartnerMaintenanceScopeDimension]
> = [
  ['MANUFACTURER', 'MODEL'],
  ['MANUFACTURER', 'ASSET'],
  ['MODEL', 'ASSET'],
  ['COMMUNE', 'ASSET'],
];

export function flattenScopeIncludeAtoms(
  rules: ScopeRuleLike[],
): ScopeIncludeFlatAtom[] {
  return flattenScopeEffectAtoms(rules).filter(
    (atom) => atom.effect === 'INCLUDE',
  );
}

function flattenScopeEffectAtoms(
  rules: ScopeRuleLike[],
): ScopeIncludeFlatAtom[] {
  const atoms: ScopeIncludeFlatAtom[] = [];
  for (const rule of rules) {
    const ruleId = rule.id ?? `${rule.dimension}:${rule.effect}`;
    const values =
      rule.values?.filter((v) => v.trim().length > 0) ??
      (rule.value?.trim() ? [rule.value.trim()] : []);
    for (const value of values) {
      atoms.push({
        ruleId,
        dimension: rule.dimension as PartnerMaintenanceScopeDimension,
        effect: rule.effect,
        value,
        interventionMode: rule.interventionMode ?? null,
      });
    }
  }
  return atoms;
}

function isModelFullyUnderManufacturer(
  modelId: string,
  manufacturerId: string,
  assets: ScopeHierarchyAsset[],
): boolean {
  const withModel = assets.filter((asset) => asset.assetModelId === modelId);
  if (withModel.length === 0) {
    return false;
  }
  return withModel.every((asset) => asset.manufacturerId === manufacturerId);
}

export function isNarrowerScopeValueCoveredByBroader(
  narrower: Pick<ScopeIncludeFlatAtom, 'dimension' | 'value'>,
  broader: Pick<ScopeIncludeFlatAtom, 'dimension' | 'value'>,
  assets: ScopeHierarchyAsset[],
): boolean {
  const assetsById = new Map(assets.map((asset) => [asset.id, asset]));

  if (broader.dimension === 'MANUFACTURER' && narrower.dimension === 'MODEL') {
    return isModelFullyUnderManufacturer(narrower.value, broader.value, assets);
  }

  if (broader.dimension === 'MANUFACTURER' && narrower.dimension === 'ASSET') {
    const asset = assetsById.get(narrower.value);
    return asset != null && asset.manufacturerId === broader.value;
  }

  if (broader.dimension === 'MODEL' && narrower.dimension === 'ASSET') {
    const asset = assetsById.get(narrower.value);
    return asset != null && asset.assetModelId === broader.value;
  }

  if (broader.dimension === 'COMMUNE' && narrower.dimension === 'ASSET') {
    const asset = assetsById.get(narrower.value);
    return asset != null && asset.cityCode === broader.value;
  }

  return false;
}

function isBroaderNarrowerPair(
  broader: PartnerMaintenanceScopeDimension,
  narrower: PartnerMaintenanceScopeDimension,
): boolean {
  return BROADER_NARROWER_PAIRS.some(
    ([b, n]) => b === broader && n === narrower,
  );
}

function modesOverlapBetweenAtoms(
  left: ScopeIncludeFlatAtom,
  right: ScopeIncludeFlatAtom,
  headerMode: PartnerMaintenanceInterventionMode | null | undefined,
): boolean {
  return scopeInterventionModesOverlap(
    resolveEffectiveScopeInterventionMode(left.interventionMode, headerMode),
    resolveEffectiveScopeInterventionMode(right.interventionMode, headerMode),
  );
}

export function findScopeIncludeHierarchyRedundancies(
  rules: ScopeRuleLike[],
  assets: ScopeHierarchyAsset[],
  headerMode: PartnerMaintenanceInterventionMode | null | undefined,
): ScopeHierarchyRedundancy[] {
  const atoms = flattenScopeIncludeAtoms(rules);
  const seen = new Set<string>();
  const redundancies: ScopeHierarchyRedundancy[] = [];

  for (const broader of atoms) {
    for (const narrower of atoms) {
      if (
        broader.ruleId === narrower.ruleId &&
        broader.value === narrower.value
      ) {
        continue;
      }
      if (!isBroaderNarrowerPair(broader.dimension, narrower.dimension)) {
        continue;
      }
      if (!modesOverlapBetweenAtoms(broader, narrower, headerMode)) {
        continue;
      }
      if (!isNarrowerScopeValueCoveredByBroader(narrower, broader, assets)) {
        continue;
      }
      const key = `${broader.ruleId}:${broader.dimension}:${broader.value}:${narrower.ruleId}:${narrower.dimension}:${narrower.value}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      redundancies.push({
        broaderRuleId: broader.ruleId,
        broaderDimension: broader.dimension,
        broaderValue: broader.value,
        narrowerRuleId: narrower.ruleId,
        narrowerDimension: narrower.dimension,
        narrowerValue: narrower.value,
      });
    }
  }

  return redundancies;
}

export function isDraftScopeValueBlockedByBroaderInclude(args: {
  selfRuleId: string;
  dimension: PartnerMaintenanceScopeDimension;
  value: string;
  ruleScopeMode: PartnerMaintenanceScopeInterventionMode | null | undefined;
  rules: ScopeRuleLike[];
  headerMode: PartnerMaintenanceInterventionMode | null | undefined;
  assets: ScopeHierarchyAsset[];
}): boolean {
  const targetAtom: ScopeIncludeFlatAtom = {
    ruleId: args.selfRuleId,
    dimension: args.dimension,
    effect: 'INCLUDE',
    value: args.value,
    interventionMode: args.ruleScopeMode ?? null,
  };

  const includeAtoms = flattenScopeIncludeAtoms(args.rules).filter(
    (atom) => atom.ruleId !== args.selfRuleId,
  );

  for (const broader of includeAtoms) {
    if (!isBroaderNarrowerPair(broader.dimension, args.dimension)) {
      continue;
    }
    if (!modesOverlapBetweenAtoms(broader, targetAtom, args.headerMode)) {
      continue;
    }
    if (
      isNarrowerScopeValueCoveredByBroader(
        { dimension: args.dimension, value: args.value },
        broader,
        args.assets,
      )
    ) {
      return true;
    }
  }

  return false;
}
